import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Order, SystemConfig, SmartSplitSuggestion, RetentionMonitorConfig } from '../types';
import { speak } from '../services/voice/speechService';
import { calculateOrderSlack } from '../services/logistics/core/timing';

/**
 * INTERFACE DE RETENÇÃO MONITORADA
 * Rastreia pedidos que foram confirmados pelo usuário para retenção
 */
export interface MonitoredRetention {
  orderId: string;
  strategyType: 'production_retention' | 'dispatch_hold';
  clusterOrderIds?: string[];  // Para dispatch_hold - IDs de todos os pedidos do cluster
  confirmedAt: number;         // Timestamp de confirmação
  initialSlack: number;         // Slack no momento da confirmação
  lastWarningAt?: number;       // Timestamp do último aviso emitido
}

/**
 * RESULTADO DO CÁLCULO DE LIBERAÇÃO
 */
export interface ReleaseTriggerResult {
  shouldRelease: boolean;
  shouldWarn: boolean;
  safeDepartureMinutes: number;  // Tempo de saída segura (pode ser negativo)
  minutesToDeadline: number;     // Minutos até ficar negativo
  reason: string;
  urgency: 'critical' | 'warning' | 'safe';
}

/**
 * CONFIGURAÇÃO DO MONITOR DE RETENÇÃO
 * Agora importado de types.ts - esta seção mantida apenas para referência
 * 
 * Configurable via SystemConfig.retentionMonitorConfig:
 * - productionMarginMinutes: Margem segurança para produção (default: 3)
 * - dispatchHoldToleranceMulti: Tolerância extra para +1 pedido (default: 10)
 * - dispatchHoldMarginMinutes: Margem segurança para dispatch hold (default: 3)
 * - checkIntervalMs: Intervalo de checagem (default: 10000ms)
 * - warnCooldownMs: Tempo mínimo entre avisos (default: 60000ms)
 */

const DEFAULT_CONFIG: RetentionMonitorConfig = {
  productionMarginMinutes: 3,
  dispatchHoldToleranceMulti: 10,
  dispatchHoldMarginMinutes: 3,
  checkIntervalMs: 10000,
  warnCooldownMs: 60000,  // 1 minuto entre avisos
};

/**
 * HOOK: useRetentionMonitor
 * Monitora pedidos retidos e dispara avisos de liberação
 */
export const useRetentionMonitor = (
  orders: Order[],
  config: SystemConfig,
  logisticAnalysis?: SmartSplitSuggestion | null
) => {
  const monitoredRetentionsRef = useRef<Map<string, MonitoredRetention>>(new Map());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCheckRef = useRef<number>(0);

  // Encontra os pedidos do cluster para um pedido - definida primeiro para uso em getConfirmedRetentions
  const findClusterForOrder = useCallback((targetOrder: Order, allOrders: Order[]): Order[] => {
    // Para dispatch hold, procuramos pedidos que estão no mesmo cluster
    // heuristics: mesmo status de 'dispatch_hold' ou mesma rota
    return allOrders.filter(o => 
      (o.acceptedStrategies || []).includes('dispatch_hold') &&
      o.id !== targetOrder.id
    );
  }, []);

  // Extrai pedidos que estão retidos (com estratégia confirmada)
  const getConfirmedRetentions = useCallback((): MonitoredRetention[] => {
    const retentions: MonitoredRetention[] = [];
    const now = Date.now();

    orders.forEach(order => {
      const acceptedStrategies = order.acceptedStrategies || [];
      
      // Production Retention
      if (acceptedStrategies.includes('production_retention')) {
        const existing = monitoredRetentionsRef.current.get(order.id);
        if (!existing) {
          // Nova retenção detectada - registrar
          const slack = calculateOrderSlack(order, config);
          retentions.push({
            orderId: order.id,
            strategyType: 'production_retention',
            confirmedAt: now,
            initialSlack: slack,
          });
          monitoredRetentionsRef.current.set(order.id, retentions[retentions.length - 1]);
        } else {
          retentions.push(existing);
        }
      }

      // Dispatch Hold - precisa verificar se faz parte de um cluster
      if (acceptedStrategies.includes('dispatch_hold')) {
        const existing = monitoredRetentionsRef.current.get(order.id);
        if (!existing) {
          // Para dispatch hold, precisamos encontrar os outros pedidos do cluster
          const clusterOrders = [order, ...findClusterForOrder(order, orders)];
          const uniqueClusterOrders = Array.from(new Map(clusterOrders.map((item) => [item.id, item])).values());
          const clusterIds = uniqueClusterOrders.map(o => o.id);

          const minSlack = Math.min(...uniqueClusterOrders.map(o => calculateOrderSlack(o, config)));
          
          retentions.push({
            orderId: order.id,
            strategyType: 'dispatch_hold',
            clusterOrderIds: clusterIds,
            confirmedAt: now,
            initialSlack: minSlack,
          });
          monitoredRetentionsRef.current.set(order.id, retentions[retentions.length - 1]);
        } else {
          retentions.push(existing);
        }
      }
    });

    // Remove pedidos que não estão mais retidos
    const currentOrderIds = new Set(orders.map(o => o.id));
    monitoredRetentionsRef.current.forEach((_, orderId) => {
      if (!currentOrderIds.has(orderId)) {
        monitoredRetentionsRef.current.delete(orderId);
      }
    });

    return retentions;
  }, [orders, config, findClusterForOrder]);

  /**
   * CALCULA O TEMPO DE SAÍDA SEGURA
   * 
   * Fórmula: slack - tempo_execução - margem
   * 
   * Para Production Retention:
   *   slack: slack do pedido retido
   *   tempo_execução: tempo_produção + tempo_entrega
   * 
   * Para Dispatch Hold:
   *   slack: menor slack do cluster
   *   tempo_execução: tempo_deslocamento_total + tempo_servico_total (+ tolerância se >1 pedido)
   */
  const calculateSafeDeparture = useCallback((
    retention: MonitoredRetention,
    allOrders: Order[]
  ): ReleaseTriggerResult => {
    const monitorConfig = config.retentionMonitorConfig || DEFAULT_CONFIG;
    const now = Date.now();

    if (retention.strategyType === 'production_retention') {
      // === PRODUCTION RETENTION ===
      const order = allOrders.find(o => o.id === retention.orderId);
      if (!order) {
        return {
          shouldRelease: false,
          shouldWarn: false,
          safeDepartureMinutes: 999,
          minutesToDeadline: 999,
          reason: 'Pedido não encontrado',
          urgency: 'safe',
        };
      }

      const slack = calculateOrderSlack(order, config);
      const elapsedMins = (now - order.createdAt) / 60000;
      
      // Tempo de produção restante (estimativa: se não está pronto, ainda está em produção)
      const isInProduction = order.status !== 'ready' && !order.isReady;
      const productionTimeRemaining = isInProduction 
        ? Math.max(0, (config.averageKitchenTime || 20) - elapsedMins)
        : 0;

      // Tempo de entrega total
      const deliveryTime = (order.estimatedDuration || 10) + (config.fleetConfig.serviceTimeMinutes || 3);
      
      // Margem de segurança
      const margin = monitorConfig.productionMarginMinutes;

      // Tempo de saída segura
      const safeDepartureMinutes = slack - productionTimeRemaining - deliveryTime - margin;
      const minutesToDeadline = safeDepartureMinutes;

      // Determinar urgência
      let urgency: 'critical' | 'warning' | 'safe' = 'safe';
      let shouldWarn = false;
      let shouldRelease = false;

      if (safeDepartureMinutes <= 0) {
        shouldRelease = true;
        urgency = 'critical';
      } else if (safeDepartureMinutes <= margin) {
        shouldWarn = true;
        urgency = 'warning';
      }

      return {
        shouldRelease,
        shouldWarn,
        safeDepartureMinutes,
        minutesToDeadline,
        reason: safeDepartureMinutes <= 0
          ? 'Tempo esgotado - liberar agora'
          : `Slack ${slack.toFixed(0)}min - Produção ${productionTimeRemaining.toFixed(0)}min - Entrega ${deliveryTime.toFixed(0)}min`,
        urgency,
      };

    } else {
      // === DISPATCH HOLD ===
      const clusterOrderIds = retention.clusterOrderIds || [retention.orderId];
      const clusterOrders = allOrders.filter(o => clusterOrderIds.includes(o.id));

      if (clusterOrders.length === 0) {
        return {
          shouldRelease: false,
          shouldWarn: false,
          safeDepartureMinutes: 999,
          minutesToDeadline: 999,
          reason: 'Cluster não encontrado',
          urgency: 'safe',
        };
      }

      // Menor slack do cluster
      const minSlack = Math.min(...clusterOrders.map(o => calculateOrderSlack(o, config)));

      // Tempo de deslocamento total (heurística baseada no número de pedidos)
      const avgTravelPerOrder = 8; // minutos
      const totalTravelTime = avgTravelPerOrder * clusterOrders.length;

      // Tempo de serviço total
      const serviceTime = (config.fleetConfig.serviceTimeMinutes || 3) * clusterOrders.length;

      // Tolerância extra para múltiplos pedidos (apenas se > 1 pedido)
      const extraTolerance = clusterOrders.length > 1 
        ? monitorConfig.dispatchHoldToleranceMulti 
        : 0;

      // Margem de segurança
      const margin = monitorConfig.dispatchHoldMarginMinutes;

      // Tempo de saída segura
      const safeDepartureMinutes = minSlack - totalTravelTime - serviceTime - extraTolerance - margin;
      const minutesToDeadline = safeDepartureMinutes;

      // Determinar urgência
      let urgency: 'critical' | 'warning' | 'safe' = 'safe';
      let shouldWarn = false;
      let shouldRelease = false;

      if (safeDepartureMinutes <= 0) {
        shouldRelease = true;
        urgency = 'critical';
      } else if (safeDepartureMinutes <= margin) {
        shouldWarn = true;
        urgency = 'warning';
      }

      return {
        shouldRelease,
        shouldWarn,
        safeDepartureMinutes,
        minutesToDeadline,
        reason: `Cluster ${clusterOrders.length} pedidos - Menor slack ${minSlack.toFixed(0)}min - Desloc ${totalTravelTime.toFixed(0)}min - Serviço ${serviceTime.toFixed(0)}min - Tolerância ${extraTolerance}min`,
        urgency,
      };
    }
  }, [config]);

  // Função helper para obter o ID de exibição correto do pedido
  const getDisplayOrderId = useCallback((orderId: string, allOrders: Order[]): string => {
    const order = allOrders.find(o => o.id === orderId);
    if (!order) return orderId.slice(-4);
    
    // Mesma lógica do OrderCard.tsx
    if (order.displayId) return order.displayId;
    if (order.shortCode) return order.shortCode;
    return order.id.slice(-4);
  }, []);

  // Emite aviso de liberação
  const emitReleaseWarning = useCallback((
    retention: MonitoredRetention,
    result: ReleaseTriggerResult,
    orderIds: string[],
    allOrders: Order[]
  ) => {
    const now = Date.now();
    const monitorConfig = config.retentionMonitorConfig || DEFAULT_CONFIG;

    // Verificar cooldown entre avisos
    if (retention.lastWarningAt && (now - retention.lastWarningAt) < monitorConfig.warnCooldownMs) {
      return; // Ainda está em cooldown
    }

    // Obter IDs de exibição corretos usando a mesma lógica do OrderCard
    const displayIds = orderIds.map(id => getDisplayOrderId(id, allOrders));
    
    const orderIdStr = displayIds.length === 1 
      ? `#${displayIds[0]}` 
      : `${displayIds.length} pedidos`;

    let message = '';
    let toastId = '';

    if (result.shouldRelease) {
      // URGÊNCIA CRÍTICA - Liberar imediato
      message = `⚠️ ATENÇÃO! ${orderIdStr}: LIBERAR AGORA! Tempo de saída seguro: ${result.minutesToDeadline.toFixed(0)} min`;
      toastId = `release-critical-${retention.orderId}`;
      
      toast.error(message, { 
        id: toastId,
        duration: 10000,
      });
      
      speak(`Atenção! ${orderIdStr}: Liberar agora! Tempo de saída crítico.`);
      
    } else if (result.shouldWarn) {
      // AVISO - Liberar em breve
      const minutesLeft = Math.ceil(result.minutesToDeadline);
      message = `⏰ ${orderIdStr}: Liberar em ${minutesLeft} min para evitar atraso`;
      toastId = `release-warning-${retention.orderId}`;
      
      toast(message, { 
        id: toastId,
        duration: 8000,
        icon: '⏰',
      });
      
      speak(`Atenção! ${orderIdStr}: Liberar em ${minutesLeft} minutos.`);
    }

    // Atualizar último aviso
    if (message) {
      retention.lastWarningAt = now;
    }
  }, [config, getDisplayOrderId]);

  // Ciclo de verificação
  const runCheck = useCallback(() => {
    const now = Date.now();
    
    // Não verificar muito frequentemente
    if (now - lastCheckRef.current < 5000) return;
    lastCheckRef.current = now;

    const retentions = getConfirmedRetentions();
    if (retentions.length === 0) return;

    retentions.forEach(retention => {
      const result = calculateSafeDeparture(retention, orders);
      
      // Para dispatch hold, pegamos todos os IDs do cluster
      const orderIds = retention.strategyType === 'dispatch_hold' && retention.clusterOrderIds
        ? retention.clusterOrderIds
        : [retention.orderId];

      if (result.shouldRelease || result.shouldWarn) {
        emitReleaseWarning(retention, result, orderIds, orders);
      }
    });

  }, [getConfirmedRetentions, calculateSafeDeparture, orders, emitReleaseWarning]);

  // Iniciar monitoramento
  useEffect(() => {
    const monitorConfig = config.retentionMonitorConfig || DEFAULT_CONFIG;
    
    intervalRef.current = setInterval(runCheck, monitorConfig.checkIntervalMs);
    
    // Primeira verificação após 3 segundos
    const initialTimeout = setTimeout(runCheck, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearTimeout(initialTimeout);
    };
  }, [runCheck, config.retentionMonitorConfig]);

  // Retorna as retentions monitoradas para possível uso externo
  return {
    getConfirmedRetentions,
    calculateSafeDeparture,
  };
};
